import React, { useState, useEffect } from 'react';
import { Gantt, ViewMode } from 'gantt-task-react';
import "gantt-task-react/dist/index.css";
import { db } from './firebaseConfig';
import { collection, onSnapshot } from 'firebase/firestore';
import { Link } from 'react-router-dom';

function Timeline() {
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState(ViewMode.Month);
  const [statusFilter, setStatusFilter] = useState('Todos');
  
  const dataPadraoInicio = new Date();
  dataPadraoInicio.setMonth(dataPadraoInicio.getMonth() - 1);
  const [dataInicioFiltro, setDataInicioFiltro] = useState(dataPadraoInicio.toISOString().split('T')[0]);
  
  const dataPadraoFim = new Date();
  dataPadraoFim.setMonth(dataPadraoFim.getMonth() + 6);
  const [dataFimFiltro, setDataFimFiltro] = useState(dataPadraoFim.toISOString().split('T')[0]);

  const [allProjects, setAllProjects] = useState([]); 

  useEffect(() => {
    return onSnapshot(collection(db, "projects"), (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllProjects(data);
    });
  }, []);

  useEffect(() => {
    if (!dataInicioFiltro || !dataFimFiltro) return;

    const janelaInicio = new Date(dataInicioFiltro + 'T00:00:00');
    const janelaFim = new Date(dataFimFiltro + 'T23:59:59');

    const filteredTasks = allProjects
      .filter(p => statusFilter === 'Todos' ? true : p.status === statusFilter)
      .map(p => {
        if (!p.startDate || !p.deadline) return null;

        const projStart = new Date(p.startDate + 'T00:00:00');
        const projEnd = p.finalizedAt ? new Date(p.finalizedAt.toDate()) : new Date(p.deadline + 'T23:59:59');

        if (projEnd < janelaInicio || projStart > janelaFim) return null;

        const visibleStart = projStart < janelaInicio ? janelaInicio : projStart;
        const visibleEnd = projEnd > janelaFim ? janelaFim : projEnd;

        return {
          start: visibleStart,
          end: visibleEnd,
          name: p.name,
          id: p.id,
          type: 'task',
          // Cores melhoradas para destacar melhor as etapas
          progress: p.status === 'Finalizado' || p.status === 'Produção' ? 100 : (p.status === 'Homologação' ? 75 : 25),
          isDisabled: true, 
          styles: { 
            progressColor: p.status === 'Produção' ? '#10B981' : p.status === 'Finalizado' ? '#9CA3AF' : '#3B82F6', 
            progressSelectedColor: '#1E3A8A' 
          }
        };
      })
      .filter(Boolean); 

    setTasks(filteredTasks);
  }, [allProjects, statusFilter, dataInicioFiltro, dataFimFiltro]);

  return (
    <div className="p-8 bg-gray-50 min-h-screen font-sans">
      <div className="flex justify-between items-center mb-6">
        <Link to="/" className="text-blue-600 font-bold hover:underline">&larr; Voltar para o Dashboard</Link>
        <div className="flex gap-2 bg-white p-1 rounded-lg shadow-sm border">
          <button onClick={() => setView(ViewMode.Day)} className={`px-4 py-1 text-sm font-bold rounded ${view === ViewMode.Day ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Dia</button>
          <button onClick={() => setView(ViewMode.Week)} className={`px-4 py-1 text-sm font-bold rounded ${view === ViewMode.Week ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Semana</button>
          <button onClick={() => setView(ViewMode.Month)} className={`px-4 py-1 text-sm font-bold rounded ${view === ViewMode.Month ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Mês</button>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm border mb-6 flex flex-wrap gap-6 items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Timeline de Projetos</h2>
          <p className="text-gray-500 text-sm">Filtre a janela de tempo que deseja visualizar.</p>
        </div>

        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">Status:</label>
            <select className="border p-2 rounded shadow-sm outline-none bg-gray-50" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option>Todos</option><option>Em desenvolvimento</option><option>Homologação</option><option>Produção</option><option>Finalizado</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">De:</label>
            <input type="date" className="border p-2 rounded shadow-sm outline-none bg-gray-50" value={dataInicioFiltro} onChange={(e) => setDataInicioFiltro(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">Até:</label>
            <input type="date" className="border p-2 rounded shadow-sm outline-none bg-gray-50" value={dataFimFiltro} onChange={(e) => setDataFimFiltro(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Container do Gráfico com estilos CSS injetados para forçar tradução da tabela e cores dos anos */}
      <div className="bg-white p-4 rounded-xl shadow-sm border overflow-x-auto relative">
        <style dangerouslySetContent={{
          __html: `
            /* 1. Traduzindo os cabeçalhos da tabela na marra via CSS (gambiarra segura) */
            .gantt-table-header div:nth-child(1) { color: transparent; position: relative; }
            .gantt-table-header div:nth-child(1)::after { content: "Nome do Projeto"; color: #374151; position: absolute; left: 10px; top: 15px; }
            
            .gantt-table-header div:nth-child(2) { color: transparent; position: relative; }
            .gantt-table-header div:nth-child(2)::after { content: "Início"; color: #374151; position: absolute; left: 10px; top: 15px; }
            
            .gantt-table-header div:nth-child(3) { color: transparent; position: relative; }
            .gantt-table-header div:nth-child(3)::after { content: "Término"; color: #374151; position: absolute; left: 10px; top: 15px; }

            /* 3. Estilizando o cabeçalho superior (Anos/Meses) */
            text.calendarBottomText { font-weight: bold; font-size: 12px !important; }
            text.calendarTopText { font-weight: 900; font-size: 14px !important; fill: #4F46E5 !important; } /* Cor roxa para o Ano */
          `
        }} />

        {tasks.length > 0 ? (
          <Gantt
            tasks={tasks}
            viewMode={view}
            locale="pt-BR" // <-- 1. TRADUÇÃO PARA PORTUGUÊS (Meses e Dias)
            listCellWidth="150px" // Largura da tabela de nomes (para não espremer muito)
            ganttHeight={450}
            columnWidth={view === ViewMode.Month ? 100 : 65} // <-- 2. AUMENTA A LARGURA na visão de mês para não truncar "Novembro"
            rowHeight={45} // Dá um pouco mais de respiro entre os projetos
            barCornerRadius={6} // Barras mais arredondadas e bonitas
            todayColor="rgba(239, 68, 68, 0.08)" // Vermelho bem clarinho para o dia de hoje
            TooltipContent={({ task }) => (
               <div className="bg-white p-3 rounded shadow-lg border text-sm z-50 relative">
                 <b className="block mb-1 text-gray-800">{task.name}</b>
                 <span className="text-gray-600">Início: {task.start.toLocaleDateString('pt-BR')}</span><br/>
                 <span className="text-gray-600">Fim: {task.end.toLocaleDateString('pt-BR')}</span>
               </div>
            )} // Tooltip personalizado e traduzido
          />
        ) : (
          <div className="h-[400px] flex items-center justify-center text-gray-500">
            Nenhum projeto encontrado para a janela de tempo e filtros selecionados.
          </div>
        )}
      </div>
    </div>
  );
}

export default Timeline;